import {
    ArrowCircleDown,
    ArrowCircleRight,
    ChangeCircleOutlined,
    CopyAll,
    Downloading,
    ForwardToInbox,
    HighlightOff,
    Login,
    ScheduleSend,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Link,
    Popover,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import queryString from 'query-string';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { PersonReadDTO, TaskVersion } from '../api/dto';
import { TaskAssignmentPreciseDTO, TaskPreciseDTO } from '../api/task';
import { FormGenerator } from '../forms/FormGenerator';
import { UiSchemaType } from '../forms/UiSchemaType';
import { useImpersonation } from '../hooks/useImpersonation';
import { copyToClipboard } from '../lib/clipboard';
import { formatDateTime } from '../lib/date';
import { translateTaskType } from '../lib/language';
import { getDocumentName, getDocumentUrl } from '../lib/records/documents/document';
import { getAssignmentLabel } from '../lib/task';
import { SelectablePersonList } from './SelectablePersonList';
import { SelectablePersonListControls } from './SelectablePersonListControls';

const InlineBox = styled(Box)({ display: 'inline' });

const DashedUnderlineTypography = styled(InlineBox)(({ theme }) => ({
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    textDecorationColor: theme.palette.text.secondary,
    cursor: 'help',
}));

const ArrowCircleDownFilled = styled(ArrowCircleRight)({
    // For some reason the filled version is missing for the down arrow
    transform: 'rotate(90deg)',
});

interface AssignmentPartProps {
    children: string;
    title?: string;
    url?: string;
}

const AssignmentPart: React.FC<AssignmentPartProps> = ({ children, title, url }) => {
    const ContentElement = title && url === undefined ? DashedUnderlineTypography : InlineBox;
    const content = <ContentElement>{children}</ContentElement>;
    return (
        <Tooltip title={title}>
            {url !== undefined ? (
                <Link href={url} target="_blank">
                    {content}
                </Link>
            ) : (
                content
            )}
        </Tooltip>
    );
};

interface TaskEscalationTimeoutProps {
    state: boolean | Date | null;
    isEscalation: boolean;
    updateTimeout: (date: Date | null) => void;
    disabled: boolean;
}

const TaskEscalationTimeout: React.FC<TaskEscalationTimeoutProps> = ({
    state,
    isEscalation,
    updateTimeout,
    disabled,
}) => {
    const intl = useIntl();
    const formatDurationFromNow = (date: Date): string => {
        // For simplicity, this method only returns the number of days, hours or minutes (whichever fits)
        const now = new Date();
        const dMs = Math.max(date.getTime() - now.getTime(), 1); // Ensure it always happens in the future
        const dMinutes = dMs / (1000 * 60);
        const dHours = dMinutes / 60;
        const dDays = dHours / 24;
        const params =
            Math.abs(dDays) >= 1
                ? ({ value: Math.ceil(dDays), unit: 'day' } as const)
                : Math.abs(dHours) >= 1
                ? ({ value: Math.ceil(dHours), unit: 'hour' } as const)
                : { value: Math.ceil(dMinutes), unit: 'minute' as const };
        return intl.formatRelativeTime(params.value, params.unit);
    };
    const labelFormatter = (date: Date) =>
        intl.formatMessage(
            {
                id: isEscalation ? 'document.task.timeoutEscalation' : 'document.task.timeoutReminder',
            },
            { when: formatDurationFromNow(date) }
        );
    const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const clearSeconds = (date: Date) => {
        const newDate = new Date(date);
        newDate.setSeconds(0, 0); // Important to also specify the milliseconds!
        return newDate;
    };
    const form = useForm({
        defaultValues: {
            date: typeof state !== 'boolean' ? state : null,
        },
    });
    const chosenDate = form.watch('date');
    const handleClose = () => {
        if (!disabled) {
            setPopoverAnchorEl(null);
        }
    };
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setPopoverAnchorEl(event.currentTarget);
        form.reset();
    };
    return (
        <Box>
            <Divider>
                <Tooltip
                    title={
                        !popoverAnchorEl
                            ? state === null
                                ? intl.formatMessage({ id: 'document.task.timeoutCanceled' })
                                : typeof state !== 'boolean'
                                ? intl.formatMessage(
                                      { id: 'document.task.timeoutScheduled' },
                                      { date: formatDateTime(state, intl) }
                                  )
                                : state
                                ? intl.formatMessage({ id: 'document.task.timeoutWaiting' })
                                : intl.formatMessage({ id: 'document.task.timeoutTriggered' })
                            : null
                    }
                >
                    <Box
                        sx={{
                            lineHeight: 0,
                            cursor: typeof state === 'boolean' || state === null ? 'help' : undefined,
                        }}
                    >
                        {state !== null && typeof state !== 'boolean' ? (
                            <IconButton size="small" onClick={handleClick} sx={{ m: -0.6 }} disabled={disabled}>
                                {isEscalation ? (
                                    <ArrowCircleDown color="primary" />
                                ) : (
                                    <ChangeCircleOutlined color="primary" />
                                )}
                            </IconButton>
                        ) : state === null ? (
                            <HighlightOff color="action" />
                        ) : state ? (
                            <Downloading color="action" />
                        ) : (
                            <ArrowCircleDownFilled color="action" />
                        )}
                    </Box>
                </Tooltip>
                <Popover
                    open={!!popoverAnchorEl}
                    anchorEl={popoverAnchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <Stack direction="column" spacing={1} sx={{ p: 2 }}>
                        <FormGenerator
                            rootElement={{
                                type: UiSchemaType.DATETIME,
                                label: intl.formatMessage({ id: 'document.field.date' }),
                                path: 'date',
                                typeProperties: {
                                    minDate: new Date(),
                                },
                            }}
                            form={form}
                            disabled={disabled}
                        />
                        <Button
                            color="primary"
                            variant="outlined"
                            startIcon={<ScheduleSend />}
                            disabled={disabled}
                            onClick={() =>
                                updateTimeout(chosenDate != null ? clearSeconds(chosenDate) : chosenDate ?? null)
                            }
                        >
                            <FormattedMessage
                                id={
                                    chosenDate != null
                                        ? 'document.task.timeoutConfirmUpdate'
                                        : 'document.task.timeoutConfirmUpdateNow'
                                }
                            />
                        </Button>
                    </Stack>
                </Popover>
            </Divider>
            {state !== null && typeof state !== 'boolean' && (
                <Typography fontSize={10} sx={{ textAlign: 'center', pointer: 'help' }}>
                    {labelFormatter(state)}
                </Typography>
            )}
        </Box>
    );
};

interface TaskAssignmentGroupTitleProps {
    assignment: TaskAssignmentPreciseDTO | null;
    count: number;
    children: React.ReactNode;
}

const TaskAssignmentGroup: React.FC<TaskAssignmentGroupTitleProps> = ({ assignment, count, children }) => {
    const intl = useIntl();
    const title = useMemo(() => {
        const richLabel = getAssignmentLabel(assignment, intl);
        return richLabel.map(({ value, ...props }, i) => (
            <AssignmentPart key={i} {...props}>
                {value}
            </AssignmentPart>
        ));
    }, [assignment, intl]);
    return (
        <>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight="medium" component="span">
                    {title}
                </Typography>
                <Chip label={count} size="small" color="default" />
            </Stack>
            {children}
        </>
    );
};

interface TaskEscalationPersonListProps {
    task: TaskPreciseDTO;
    selection: Set<number>;
    updateSelection: (newSelection: Set<number>) => void;
    updateTimeout: (data: { date: Date | null; isEscalation: boolean }) => void;
    readOnly: boolean;
    disabled: boolean;
}

export const TaskEscalationPersonList: React.FC<TaskEscalationPersonListProps> = ({
    task,
    selection,
    updateSelection,
    updateTimeout,
    readOnly,
    disabled,
}) => {
    const intl = useIntl();

    const { canImpersonateUsers, isCurrentlyImpersonating, impersonate, isImpersonateLoading } = useImpersonation();

    // May contain duplicates
    const assigneesFlat = useMemo(() => task.assignees.flat(2).map(({ person }) => person), [task.assignees]);
    const selectedAssigneesDistinctInOrder = useMemo(() => {
        // Returns the selected persons in order, deduplicated
        const persons: PersonReadDTO[] = [];
        const alreadyAdded = new Set<number>();
        assigneesFlat.forEach((person) => {
            if (selection.has(person.id) && !alreadyAdded.has(person.id)) {
                persons.push(person);
                alreadyAdded.add(person.id);
            }
        });
        return persons;
    }, [assigneesFlat, selection]);
    const handleMailAddressesCopy = useCallback(
        () =>
            copyToClipboard(
                selectedAssigneesDistinctInOrder
                    .map(({ email }) => email)
                    .filter((email): email is string => !!email)
                    .map((email) => email.toLowerCase())
                    .join(', ')
            ),
        [selectedAssigneesDistinctInOrder, copyToClipboard]
    );
    const handleMailToOpen = useCallback(() => {
        const addresses = selectedAssigneesDistinctInOrder
            .map(({ email }) => email)
            .filter((email): email is string => !!email)
            .map((email) => email.toLowerCase())
            .join(',');
        const subject = `${getDocumentName(task, intl)} - ${translateTaskType(task.type, intl.locale).typeName}`;
        const body = `${window.location.origin}${getDocumentUrl(task)}`;
        const params = queryString.stringify({
            subject,
            body,
        });
        const mailto = `mailto:${encodeURIComponent(addresses)}?${params}`;
        window.open(mailto, '_blank');
    }, [selectedAssigneesDistinctInOrder, intl]);
    const handleImpersonate = useCallback(() => {
        if (selectedAssigneesDistinctInOrder.length === 1) {
            const person = selectedAssigneesDistinctInOrder[0];
            impersonate({ impersonatingUsername: person.username });
        }
    }, [selectedAssigneesDistinctInOrder, impersonate]);

    return (
        <Stack direction="column" spacing={2}>
            {task.assignees
                .map((assignments, sequenceIndex) => ({
                    assignments,
                    group:
                        task.taskVersion === TaskVersion.V2 && sequenceIndex !== task.assignment.groups.length
                            ? task.assignment.groups[sequenceIndex]
                            : null,
                }))
                .map(({ assignments, group }, sequenceIndex) => (
                    <Fragment key={sequenceIndex}>
                        {(sequenceIndex < task.assignment?.groups?.length ?? 0
                            ? assignments.map((assignment, groupIndex) => {
                                  return {
                                      // Pool or sequence
                                      groupAssignees: assignments[group === null || group.sequence ? 0 : groupIndex],
                                      assignment: group,
                                  };
                              })
                            : [
                                  {
                                      // Added assignees
                                      groupAssignees: assignments.flat(),
                                      assignment: null,
                                  },
                              ].filter((v) => v.groupAssignees.length > 0)
                        )
                            .map(({ groupAssignees, assignment }) => ({
                                // Inject persons metadata
                                groupAssigneePersons: groupAssignees.map(({ person }) => person),
                                assignment,
                                metadata: Object.fromEntries(
                                    groupAssignees.map(
                                        ({
                                            person: { id },
                                            hasBeenNotified,
                                            //isLastNotified,
                                            isDuplicate,
                                        }) => [
                                            id,
                                            {
                                                light: isDuplicate || !hasBeenNotified,
                                                striked: !hasBeenNotified,
                                            },
                                        ]
                                    )
                                ),
                            }))
                            .map(({ groupAssigneePersons, assignment, metadata }, groupIndex) => (
                                <TaskAssignmentGroup
                                    key={groupIndex}
                                    assignment={assignment !== null ? assignment.assignments[groupIndex] : null}
                                    count={groupAssigneePersons.length}
                                >
                                    {groupAssigneePersons.length > 0 ? (
                                        <SelectablePersonList
                                            key={groupIndex}
                                            persons={groupAssigneePersons}
                                            personsMetadata={metadata}
                                            selection={selection}
                                            updateSelection={updateSelection}
                                            disabled={disabled || isImpersonateLoading}
                                        />
                                    ) : (
                                        <Alert severity="info">
                                            <FormattedMessage id="document.task.assignees.group.empty" />
                                        </Alert>
                                    )}
                                </TaskAssignmentGroup>
                            ))}
                        {group !== null &&
                            !task.taskSilent &&
                            (group.timeoutDisplayEscalation || task.code.toString() === 'ACTIVITY_SURFACE_TSO') && (
                                <TaskEscalationTimeout
                                    state={
                                        group.timeoutTriggered
                                            ? false
                                            : group.timeoutDueDate !== null
                                            ? new Date(group.timeoutDueDate)
                                            : task.taskActive
                                            ? true
                                            : null
                                    }
                                    isEscalation={group.timeoutDisplayEscalation}
                                    updateTimeout={(date) =>
                                        updateTimeout({
                                            date,
                                            isEscalation: group.timeoutTypeEscalation,
                                        })
                                    }
                                    disabled={readOnly || disabled}
                                />
                            )}
                    </Fragment>
                ))}
            <SelectablePersonListControls
                persons={assigneesFlat}
                selection={selection}
                setSelected={updateSelection}
                otherActions={
                    <>
                        <Tooltip title={intl.formatMessage({ id: 'document.task.openMailto' })}>
                            <IconButton color="primary" onClick={handleMailToOpen}>
                                <ForwardToInbox />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={intl.formatMessage({ id: 'document.task.copySelectedEmails' })}>
                            <IconButton color="primary" onClick={handleMailAddressesCopy}>
                                <CopyAll />
                            </IconButton>
                        </Tooltip>
                        {(canImpersonateUsers || isCurrentlyImpersonating) && selection.size === 1 && (
                            <Tooltip title={intl.formatMessage({ id: 'menu.loginAs' })}>
                                <span>
                                    <IconButton
                                        color="primary"
                                        onClick={handleImpersonate}
                                        disabled={disabled || isImpersonateLoading}
                                    >
                                        <Login />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </>
                }
            />
        </Stack>
    );
};
